import React from 'react';
import { Shield, Eye, Lock, Users, Database, Mail } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { SEO } from '@/components/SEO';

const PrivacyPage = () => {
  return (
    <>
      <SEO 
        pageType="page"
        title="Privacy Policy - GameHub"
        description="Learn about how GameHub protects your privacy and handles your personal information."
        keywords="privacy policy, data protection, user privacy, gaming privacy"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-5xl font-bold text-gray-900">
                Privacy <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Policy</span>
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: June 14, 2025</p>
          </div>

          <div className="space-y-8">
            {/* Information We Collect */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Database className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Information We Collect</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>IP address and general location information</li>
                    <li>Browser type and version</li>
                    <li>Device information (type, operating system)</li>
                    <li>Pages visited and time spent on our site</li>
                    <li>Referring website information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Information You Provide</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-600">
                    <li>Account information (if you create an account)</li>
                    <li>Email address for newsletters (optional)</li>
                    <li>Game preferences and saved progress</li>
                    <li>Comments or feedback you submit</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* How We Use Your Information */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Eye className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">How We Use Your Information</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>To provide and maintain our gaming services</li>
                  <li>To improve user experience and website functionality</li>
                  <li>To analyze usage patterns and optimize performance</li>
                  <li>To send occasional updates about new games (if subscribed)</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To prevent fraud and ensure website security</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </div>
            </Card>

            {/* Data Protection */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Lock className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Data Protection & Security</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>We implement appropriate security measures to protect your personal information:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>SSL encryption for data transmission</li>
                  <li>Secure database storage with access controls</li>
                  <li>Regular security audits and updates</li>
                  <li>Limited access to personal data by authorized personnel only</li>
                  <li>Data backup and recovery procedures</li>
                </ul>
                <p className="text-sm text-gray-600 mt-4">
                  <strong>Note:</strong> No method of transmission over the internet is 100% secure. 
                  While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </div>
            </Card>

            {/* Information Sharing */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Users className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Information Sharing</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>We do not sell, trade, or rent your personal information. We may share information in these limited circumstances:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li>With your explicit consent</li>
                  <li>To comply with legal requirements or court orders</li>
                  <li>To protect our rights, property, or safety</li>
                  <li>With trusted service providers who assist in our operations (under strict confidentiality agreements)</li>
                  <li>In connection with a business transfer or merger</li>
                </ul>
              </div>
            </Card>

            {/* Cookies & Tracking */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Eye className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Cookies & Tracking Technologies</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>We use cookies and similar technologies to enhance your experience:</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">Essential Cookies</h4>
                    <p className="text-sm text-gray-600">Required for basic website functionality</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Analytics Cookies</h4>
                    <p className="text-sm text-gray-600">Help us understand how you use our site</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Preference Cookies</h4>
                    <p className="text-sm text-gray-600">Remember your settings and preferences</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Advertising Cookies</h4>
                    <p className="text-sm text-gray-600">Show relevant ads and measure effectiveness</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Your Rights */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Your Rights</h2>
              </div>
              <div className="space-y-4 text-gray-700">
                <p>You have the following rights regarding your personal information:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                  <li><strong>Portability:</strong> Receive your data in a portable format</li>
                  <li><strong>Objection:</strong> Object to certain types of processing</li>
                  <li><strong>Restriction:</strong> Request limitation of processing</li>
                </ul>
              </div>
            </Card>

            {/* Contact Information */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <div className="flex items-center mb-6">
                <Mail className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-2xl font-bold text-gray-900">Contact Us</h2>
              </div>
              <div className="text-gray-700">
                <p className="mb-4">
                  If you have questions about this privacy policy or want to exercise your rights, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p><strong>Email:</strong> privacy@gamehub.com</p>
                  <p><strong>Response Time:</strong> We will respond within 30 days</p>
                </div>
              </div>
            </Card>

            {/* Updates */}
            <Card className="p-8 bg-white border-0 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Policy Updates</h2>
              <div className="text-gray-700">
                <p>
                  We may update this privacy policy periodically. When we make significant changes, 
                  we will notify you by posting the updated policy on this page and updating the 
                  "Last updated" date at the top of this policy.
                </p>
                <p className="mt-4 text-sm text-gray-600">
                  Your continued use of our website after any changes indicates your acceptance of the updated policy.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;
